import GobodoConfig from '../config/gobodoConfig';
import { storage } from './storage';
import * as GobodoApi from "@gobodo/gobodo-api-promise";
import { keycloakService } from '../services/keycloakService';

// ---------------------------------------------------------------------------
// HTTP-level concurrency limiter.
// The Gobodo/Neo4j backend cannot handle many concurrent requests — they
// collide on shared transactions and return 500s.  Instead of serialising
// everything (too slow) we cap at MAX_CONCURRENT in-flight HTTP requests.
// Applied once via callApi monkey-patch so every SDK call is covered.
// ---------------------------------------------------------------------------
const MAX_CONCURRENT = 4;
let _active = 0;
const _waiting: (() => void)[] = [];

function _acquire(): Promise<void> {
    if (_active < MAX_CONCURRENT) { _active++; return Promise.resolve(); }
    return new Promise<void>(r => _waiting.push(() => { _active++; r(); }));
}
function _release(): void {
    _active--;
    if (_waiting.length > 0) _waiting.shift()!();
}

function patchCallApi(client: any): void {
    if ((client as any)._concurrencyPatched) return;
    (client as any)._concurrencyPatched = true;
    const orig = client.callApi.bind(client);
    client.callApi = function (...args: any[]) {
        return _acquire().then(() =>
            (orig(...args) as Promise<any>).finally(_release)
        );
    };
}
// ---------------------------------------------------------------------------

export function getGobodoClient(gobodo_api_url?: string) {
    if (!GobodoConfig.apiclient) {
        let defaultClient = GobodoApi.ApiClient.instance;
        defaultClient.defaultHeaders = {}
        defaultClient.enableCookies = true;

        if(gobodo_api_url){
            defaultClient.basePath = gobodo_api_url+"/graph/data";
        }else{
            defaultClient.basePath = GobodoConfig.gobodo_api_url()+"/graph/data";
        }
        defaultClient.timeout = 120000;

        patchCallApi(defaultClient);
        GobodoConfig.apiclient = defaultClient;
    }

    // Always sync latest token before returning (handles initial load, refresh, expiry)
    const keycloakToken = keycloakService.getToken();
    const envToken = process.env.REACT_APP_GOBODO_AUTH_TOKEN || process.env.GOBODO_AUTH_TOKEN || process.env.GOBODO_JWT;
    const rawToken = keycloakToken || storage.getItem(GobodoConfig.auth_storage_key) || envToken;
    const authToken = rawToken && !String(rawToken).startsWith('Bearer ') ? `Bearer ${rawToken}` : rawToken;

    if (authToken) {
        GobodoConfig.authtoken = authToken;
        GobodoConfig.apiclient.authentications["NexusGraphAPIAuth"].apiKey = authToken;
        GobodoConfig.apiclient.defaultHeaders["Authorization"] = authToken;
    } else if (GobodoConfig.apiclient) {
        delete GobodoConfig.apiclient.defaultHeaders["Authorization"];
    }

    return GobodoConfig.apiclient;
}

/**
 * Sync the current Keycloak token to Gobodo API client
 * Call this whenever the token changes (on login, token refresh, etc.)
 */
export function syncGobodoAuthToken(): void {
    const keycloakToken = keycloakService.getToken();

    if (keycloakToken && GobodoConfig.apiclient) {
        const authToken = !String(keycloakToken).startsWith('Bearer ') ? `Bearer ${keycloakToken}` : keycloakToken;
        GobodoConfig.authtoken = authToken;
        GobodoConfig.apiclient.authentications["NexusGraphAPIAuth"].apiKey = authToken;
        GobodoConfig.apiclient.defaultHeaders["Authorization"] = authToken;
        storage.setItem(GobodoConfig.auth_storage_key, keycloakToken);
    } else if (!keycloakToken && GobodoConfig.apiclient) {
        GobodoConfig.authtoken = null;
        GobodoConfig.apiclient.authentications["NexusGraphAPIAuth"].apiKey = undefined;
        delete GobodoConfig.apiclient.defaultHeaders["Authorization"];
    }
}

export function getGobodoAuthToken(): string | null | undefined {
    // Prefer Keycloak token
    const keycloakToken = keycloakService.getToken();
    if (keycloakToken) {
        return keycloakToken;
    }
    
    // Fallback to storage
    const storedToken = storage.getItem(GobodoConfig.auth_storage_key);
    if (storedToken) {
        GobodoConfig.authtoken = storedToken;
        return storedToken;
    }

    // Dev mode fallback to env var
    const envToken = process.env.REACT_APP_GOBODO_AUTH_TOKEN || process.env.GOBODO_AUTH_TOKEN || process.env.GOBODO_JWT;
    if (envToken) {
        GobodoConfig.authtoken = envToken;
        return envToken;
    }

    return GobodoConfig.authtoken;
}

export async function storeRegistrationToken(token: string, deviceType: string = "WEB"): Promise<void> {
    const gobodoApi = getGobodoClient();
    const apiInstance = new GobodoApi.NotificationsApi(gobodoApi);
    const deviceRegistration = new GobodoApi.DeviceRegistrationRequest({
        notificationToken: token,
        deviceType: deviceType
    });
    await apiInstance.registerDevice(deviceRegistration);
} 